import { Cake, Camera, CreditCard } from 'lucide-react'
import { useState } from 'react'

import {
  formatAge,
  type GetMeResponse,
  type PublicSubscription,
  SUBSCRIPTION_PLAN_LABELS,
} from '@snootsvet/shared/portal'

import { SubscriptionStatusBadge } from '@/customer/components/billing/subscription-status-badge'
import { PetPhotoDialog } from '@/customer/components/pet/pet-photo-dialog'
import { Card, CardContent } from '@/customer/components/ui/card'
import { cn } from '@/customer/lib/utils'

import { SpeciesIcon } from './species-icon'

/** Pet and membership summary. */
export function PetHeroCard({
  pet,
  subscription,
  className,
}: {
  pet: GetMeResponse['patients'][number]
  /**
   * Live subscription state:
   *
   * - `undefined` - loading / passive; no membership row rendered.
   * - `PublicSubscription` - active subscription; renders the plan
   *   label plus a status badge when the sub needs attention.
   * - `null` - no subscription on file.
   */
  subscription?: PublicSubscription | null
  className?: string
}) {
  const [editingPhoto, setEditingPhoto] = useState(false)
  const initial = pet.name.trim().charAt(0).toUpperCase() || '?'
  const age = pet.dateOfBirth ? formatAge(pet.dateOfBirth) : null
  const isSingleWord = !pet.name.trim().includes(' ')

  return (
    <Card
      color="muted"
      shadow={pet.sex === 'male' ? 'baby-blue' : 'pink'}
      className={cn('relative rounded-xl', className)}
    >
      <CardContent>
        <div className="flex items-stretch gap-4">
          <button
            type="button"
            aria-label={`Edit ${pet.name}’s photo`}
            onClick={() => setEditingPhoto(true)}
            className={cn(
              'relative flex size-16 md:size-20 shrink-0 items-center justify-center rounded-2xl cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-4',
              pet.sex === 'male' ? 'bg-baby-blue' : 'bg-pink'
            )}
          >
            {pet.photoUrl ? (
              <img
                src={pet.photoUrl}
                alt={pet.name}
                className="size-full rounded-2xl object-cover"
              />
            ) : (
              <span className="text-headline-lg-mobile md:text-headline-lg -translate-y-[2px]">
                {initial}
              </span>
            )}
            <span className="absolute -bottom-1 -right-1 rounded-full bg-background border-foreground border-[1.5px] text-foreground p-1">
              <Camera className="size-4 stroke-[2.5px]" aria-hidden />
            </span>
          </button>

          <div className="flex min-w-0 flex-1 flex-col justify-center overflow-hidden">
            <h2
              className={cn(
                'text-headline-md',
                isSingleWord ? 'truncate' : 'line-clamp-2 wrap-anywhere'
              )}
            >
              {pet.name}
            </h2>
          </div>
        </div>
        <div className="pl-1 xs:pl-1.5 flex flex-col gap-y-2 text-base font-bold">
          {pet.breed ? (
            <p className="flex items-center gap-2">
              <SpeciesIcon species={pet.species} size="20" className="shrink-0" />
              {pet.breed}
            </p>
          ) : null}
          {age ? (
            <p className="flex items-center gap-2">
              <Cake className="size-5 shrink-0" />
              <span>{age}</span>
            </p>
          ) : null}
          {subscription === null ? (
            <p className="flex items-center gap-2">
              <CreditCard className="size-5 shrink-0" />
              <span>No active plan</span>
            </p>
          ) : subscription?.plan ? (
            <p className="flex items-center gap-2">
              <CreditCard className="size-5 shrink-0" />
              <span>{SUBSCRIPTION_PLAN_LABELS[subscription.plan]} plan</span>
              <SubscriptionStatusBadge subscription={subscription} size="sm" />
            </p>
          ) : null}
        </div>
      </CardContent>
      {editingPhoto && <PetPhotoDialog pet={pet} onClose={() => setEditingPhoto(false)} />}
    </Card>
  )
}
